import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Container, Grid, Button, Card, Typography, TextField, Box, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, FormControl, InputLabel, Select, MenuItem, CircularProgress,
  TablePagination, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Divider, Accordion, AccordionSummary, AccordionDetails,
  Tooltip, Badge, Stack, Checkbox, Collapse, CardContent, Avatar, Drawer
} from "@mui/material";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import FilterAltIcon from "@mui/icons-material/FilterAltOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import Timeline from '@mui/icons-material/Timeline';
import PersonAdd from '@mui/icons-material/PersonAdd';
import Refresh from '@mui/icons-material/Refresh';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WarningIcon from "@mui/icons-material/Warning";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import GroupsIcon from "@mui/icons-material/Groups";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BlockIcon from "@mui/icons-material/Block";
import TuneIcon from "@mui/icons-material/Tune";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useNavigate } from "react-router-dom";
import HistorialDepartamento from "./HistoricoRepresentantes";

import { apiService, handleApiError } from '../config/api';

const theme = {
  primary: {
    main: '#7E5A3C',
    light: '#9C8C80',
    dark: '#5D4030',
    contrastText: '#FFFFFF'
  },
  secondary: {
    main: '#A67C52',
    light: '#C9B199',
    dark: '#8A6642',
    contrastText: '#FFFFFF'
  },
  background: {
    default: '#F4F3F1',
    paper: '#FFFFFF',
    card: '#FBFAF9'
  },
  text: {
    primary: '#333333',
    secondary: '#666666',
    disabled: '#999999',
    hint: '#999999'
  },
  error: {
    main: '#B54B3C',
    light: '#D48178',
    dark: '#923B30',
    contrastText: '#FFFFFF'
  },
  success: {
    main: '#5A8151',
    light: '#8AAA83',
    dark: '#3F6637',
    contrastText: '#FFFFFF'
  }
};

const ESTATUS_COLOR = {
  VIGENTE: theme.success.main,
  VENCIDA: theme.error.main,
  INHABILITADO: theme.text.disabled
};

const COLUMN_GROUPS = [
  { title: 'General y ubicación', fields: ['estatus', 'nombre_representante', 'dni', 'categorias', 'nombre_asociacion', 'NombreDepartamento', 'NombreMunicipio', 'domicilio', 'ubicacion_finca'] },
  { title: 'Contacto y elección', fields: ['telefono', 'fecha_eleccion', 'fecha_vencimiento', 'estadoEleccion', 'fecha_certificacion_punto_acta'] },
  { title: 'Acta y documentos', fields: ['clave_IHCAFE', 'cod_cooperativa', 'numero', 'tomo', 'libro', 'fecha_constancia', 'reporte_cosecha'] },
  { title: 'Otros', fields: ['observaciones', 'fotografia'] },
];

const DetailSection = ({ title, children }) => (
  <Box>
    <Typography
      variant="overline"
      sx={{
        color: theme.secondary.main,
        fontWeight: 700,
        letterSpacing: '0.08em',
        display: 'block',
        mb: 1
      }}
    >
      {title}
    </Typography>
    <Stack spacing={1}>
      {children}
    </Stack>
  </Box>
);

const DetailField = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
    <Typography variant="body2" sx={{ color: theme.text.secondary, flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ color: theme.text.primary, fontWeight: 500, textAlign: 'right' }}>
      {value || '—'}
    </Typography>
  </Box>
);

const Reporteria = () => {
  const [representantes, setRepresentantes] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [municipiosFiltrados, setMunicipiosFiltrados] = useState([]);
  const [asociaciones, setAsociaciones] = useState([]);
  const [cooperativas, setCooperativas] = useState([]);
  const [filters, setFilters] = useState({
    search: "",
    asociacion: "",
    departamento: "",
    municipio: "",
    fechaInicial: "",
    fechaFinal: "",
    estatus: [],
    estadoEleccion: [],
  });
  const [selectedColumns, setSelectedColumns] = useState({});
  const [loading, setLoading] = useState(true);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const iframeRef = useRef(null);
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [selectedRepresentante, setSelectedRepresentante] = useState(null);
  const [camposFaltantes, setCamposFaltantes] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Estados para análisis histórico
  const [mostrarHistorico, setMostrarHistorico] = useState(false);
  const [representantesNuevos, setRepresentantesNuevos] = useState([]);
  const [representantesReelectos, setRepresentantesReelectos] = useState([]);
  const [estadisticasHistorico, setEstadisticasHistorico] = useState({
    totalNuevos: 0,
    totalReelectos: 0,
    promedioReelecciones: 0
  });
  const [tabHistorico, setTabHistorico] = useState(0);

  const tieneCategoria3 = (representante) => {
    if (!representante || !representante.categorias || !Array.isArray(representante.categorias)) {
      return false;
    }
    return representante.categorias.some(categoria =>
      categoria && (categoria.Id === 3 || categoria.CategoriaFK === 3) && categoria.Estado !== 'Inactivo'
    );
  };

  const handleRowClick = (representante) => {
    if (selectedRepresentante && selectedRepresentante.cod_representante === representante.cod_representante) {
      handleDetailClose();
      return;
    }

    setSelectedRepresentante(representante);

    const missingFields = getMissingFields(representante);
    setCamposFaltantes(missingFields);
  };

  const handleDetailClose = () => {
    setSelectedRepresentante(null);
    setCamposFaltantes([]);
  };

  const handleStatusChange = (value, filterType = 'estatus') => {
    setFilters((prevFilters) => {
      const currentFilter = prevFilters[filterType] || [];
      const newFilter = currentFilter.includes(value)
        ? currentFilter.filter((s) => s !== value)
        : [...currentFilter, value];
      return { ...prevFilters, [filterType]: newFilter };
    });
  };

  const columns = [
    { field: "estatus", header: "Estatus" },
    { field: "nombre_representante", header: "Nombre Representante" },
    { field: "nombre_asociacion", header: "Asociación" },
    { field: "dni", header: "DNI" },
    { field: "categorias", header: "Categorías" },
    { field: "NombreDepartamento", header: "Departamento" },
    { field: "NombreMunicipio", header: "Municipio" },
    { field: "domicilio", header: "Domicilio" },
    { field: "ubicacion_finca", header: "Ubicación Finca" },
    { field: "telefono", header: "Teléfono" },
    { field: "fecha_certificacion_punto_acta", header: "Fecha Certificación" },
    { field: "clave_IHCAFE", header: "Clave IHCAFE" },
    { field: "reporte_cosecha", header: "Reporte Cosecha" },
    { field: "fecha_eleccion", header: "Fecha Elección" },
    { field: "fecha_vencimiento", header: "Fecha Vencimiento" },
    { field: "estadoEleccion", header: "Estado Elección" },
    { field: "observaciones", header: "Observaciones" },
    { field: "fotografia", header: "Fotografía" },
    { field: "cod_cooperativa", header: "Cooperativa" },
    { field: "numero", header: "Número" },
    { field: "tomo", header: "Tomo" },
    { field: "libro", header: "Libro" },
    { field: "fecha_constancia", header: "Fecha Constancia CONSUCOOP" },
  ];


// CLASIFICACIÓN CORRECTA: ¿Es el primer período de este DNI? (sin importar asociación)
const clasificarRepresentantes = useMemo(() => {
  // Incluir todos los representantes, no solo los que tienen DNI
  const todosRepresentantes = representantes;

  // Filtrar solo los que tienen DNI válido para agrupar
  const representantesConDNI = representantes.filter(r => r.dni && r.dni.trim() !== "");

  // Agrupar SOLO por DNI (historial global de la persona)
  const repsPorDNI = {};
  representantesConDNI.forEach(rep => {
    const key = rep.dni; // Solo DNI, sin asociación
    if (!repsPorDNI[key]) repsPorDNI[key] = [];
    repsPorDNI[key].push({
      ...rep,
      fecha_eleccion_date: rep.fecha_eleccion ? new Date(rep.fecha_eleccion) : null
    });
  });

  // Ordenar cada grupo por fecha
  Object.values(repsPorDNI).forEach(grupo => {
    grupo.sort((a, b) =>
      (a.fecha_eleccion_date && b.fecha_eleccion_date)
        ? a.fecha_eleccion_date - b.fecha_eleccion_date
        : 0
    );
  });

  // Marcar si es el primer registro de este DNI (globalmente)
  return todosRepresentantes.map(rep => {
    // Si no tiene DNI, marcarlo como "Nuevo" por defecto (no podemos determinar reelección)
    if (!rep.dni || rep.dni.trim() === "") {
      return {
        ...rep,
        estadoEleccion: "Nuevo"
      };
    }

    const key = rep.dni;
    const grupo = repsPorDNI[key] || [];
    const esPrimero = grupo.findIndex(r =>
      r.fecha_eleccion === rep.fecha_eleccion &&
      r.cod_representante === rep.cod_representante
    ) === 0;

    return {
      ...rep,
      estadoEleccion: esPrimero ? "Nuevo" : "Reelegido"
    };
  });
}, [representantes]);



  useEffect(() => {
  if (representantes.length === 0) return;

  // Agrupar SOLO por DNI (historial global de la persona)
  const todosPorDNI = {};
  representantes.forEach(rep => {
    // Solo agrupar los que tienen DNI válido
    if (!rep.dni || rep.dni.trim() === "") return;
    // Clave: solo DNI
    const key = rep.dni;
    if (!todosPorDNI[key]) todosPorDNI[key] = [];
    todosPorDNI[key].push({
      ...rep,
      fecha_eleccion_date: rep.fecha_eleccion ? new Date(rep.fecha_eleccion) : null
    });
  });

  // Ordenar por fecha para determinar el primer período de cada DNI
  Object.values(todosPorDNI).forEach(grupo => {
    grupo.sort((a, b) => (a.fecha_eleccion_date && b.fecha_eleccion_date)
      ? a.fecha_eleccion_date - b.fecha_eleccion_date
      : 0
    );
  });

  // Crear mapa para marcar el primer período de cada DNI (sin importar asociación)
  const mapaReeleccion = new Map();
  Object.entries(todosPorDNI).forEach(([dni, periodos]) => {
    periodos.forEach((p, i) => {
      const fechaISO = p.fecha_eleccion
        ? new Date(p.fecha_eleccion).toISOString().split('T')[0]
        : 'SIN_FECHA';
      // Clave única: DNI + Asociación + Fecha (para identificar el registro específico)
      const key = `${p.dni}_${p.cod_asociacion}_${fechaISO}`;
      mapaReeleccion.set(key, {
        esReeleccion: i > 0, // Solo el primer período del DNI es nuevo, todos los demás son reelección
        numeroPeriodo: i + 1,
        totalPeriodos: periodos.length
      });
    });
  });

  let filtrados = [...representantes];
  if (filters.departamento) filtrados = filtrados.filter(r => String(r.cod_depto) === String(filters.departamento));
  if (filters.municipio) filtrados = filtrados.filter(r => String(r.cod_municipio) === String(filters.municipio));
  if (filters.asociacion) filtrados = filtrados.filter(r => String(r.cod_asociacion) === String(filters.asociacion));

  const nuevos = [];
  const reelectos = [];

  filtrados.forEach(rep => {
    // Si no tiene DNI, tratarlo como nuevo
    if (!rep.dni || rep.dni.trim() === "") {
      nuevos.push({
        ...rep,
        tipo: 'NUEVO',
        numeroPeriodo: 1,
        totalPeriodos: 1,
        vecesReelecto: 0
      });
      return;
    }

    const fechaISO = rep.fecha_eleccion
      ? new Date(rep.fecha_eleccion).toISOString().split('T')[0]
      : 'SIN_FECHA';
    const key = `${rep.dni}_${rep.cod_asociacion}_${fechaISO}`;
    const info = mapaReeleccion.get(key) || { esReeleccion: false, numeroPeriodo: 1, totalPeriodos: 1 };

    const analisis = {
      ...rep,
      tipo: info.esReeleccion ? 'REELECCIÓN' : 'NUEVO',
      numeroPeriodo: info.numeroPeriodo,
      totalPeriodos: info.totalPeriodos,
      vecesReelecto: info.esReeleccion ? info.numeroPeriodo - 1 : 0
    };

    if (info.esReeleccion) {
      reelectos.push(analisis);
    } else {
      nuevos.push(analisis);
    }
  });

  let nuevosFiltrados = nuevos;
  let reelectosFiltrados = reelectos;
  if (filters.estadoEleccion.length > 0) {
    if (filters.estadoEleccion.includes('Nuevo') && !filters.estadoEleccion.includes('Reelegido')) {
      reelectosFiltrados = [];
    } else if (filters.estadoEleccion.includes('Reelegido') && !filters.estadoEleccion.includes('Nuevo')) {
      nuevosFiltrados = [];
    }
  }

  setRepresentantesNuevos(nuevosFiltrados);
  setRepresentantesReelectos(reelectosFiltrados);

  const sumaReelecciones = reelectosFiltrados.reduce((sum, r) => sum + r.vecesReelecto, 0);
  const promedio = reelectosFiltrados.length > 0
    ? (sumaReelecciones / reelectosFiltrados.length).toFixed(2)
    : '0.00';

  setEstadisticasHistorico({
    totalNuevos: nuevosFiltrados.length,
    totalReelectos: reelectosFiltrados.length,
    promedioReelecciones: promedio
  });
}, [representantes, filters]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const datosReporte = await apiService.utils.getDatosReporte();

        const {
          representantes: repsData,
          asociaciones: asociacionData,
          departamentos: deptoData,
          municipios: municipioData,
          cooperativas: cooperativaData
        } = datosReporte;

        const representantesCategoria3 = repsData.filter(rep => tieneCategoria3(rep));

        setRepresentantes(representantesCategoria3);
        setDepartamentos(deptoData);
        setMunicipios(municipioData);
        setAsociaciones(asociacionData);
        setCooperativas(cooperativaData);

        setSnackbarMessage(`${representantesCategoria3.length} representantes municipales cargados exitosamente.`);
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
      } catch (error) {
        const errorMessage = handleApiError(error, "Error al cargar los datos");
        setSnackbarMessage(errorMessage);
        setSnackbarSeverity("error");
        setOpenSnackbar(true);

        setRepresentantes([]);
        setDepartamentos([]);
        setMunicipios([]);
        setAsociaciones([]);
        setCooperativas([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getNombreDepartamento = (cod_depto) => {
    const depto = departamentos.find((d) => d.CodigoDepartamento === cod_depto);
    return depto ? depto.NombreDepartamento : "";
  };

  const getNombreMunicipio = (cod_municipio) => {
    const municipio = municipios.find((m) => m.CodigoMunicipio === cod_municipio);
    return municipio ? municipio.NombreMunicipio : "";
  };

  const getNombreAsociacion = (cod_asociacion) => {
    const asociacion = asociaciones.find(
      (a) => a.cod_asociacion === cod_asociacion
    );
    return asociacion ? asociacion.nombre_abreviado : "";
  };

  const getNombreCooperativa = (cod_cooperativa) => {
    const cooperativa = cooperativas.find(
      (coop) => coop.cod_cooperativa === cod_cooperativa
    );
    return cooperativa ? cooperativa.nombre_cooperativa : "No asociado";
  };

  const getCategoriasTexto = (representante) => {
    const categoriasActivas = (representante.categorias || []).filter((cat) => cat.Estado !== 'Inactivo');
    if (categoriasActivas.length === 0) return "Sin categorías";
    return categoriasActivas
      .map((cat) => cat.Cargo ? `${cat.Descripcion} (${cat.Cargo})` : cat.Descripcion)
      .join(", ");
  };

  const toggleColumnSelection = (column) => {
    setSelectedColumns((prevSelectedColumns) => {
      const updatedColumns = { ...prevSelectedColumns };
      if (updatedColumns[column.field]) {
        delete updatedColumns[column.field];
      } else {
        updatedColumns[column.field] = column;
      }
      return updatedColumns;
    });
  };

  useEffect(() => {
    if (filters.departamento) {
      const filteredMunicipios = municipios.filter(
        (mun) => String(mun.CodigoDepartamento) === String(filters.departamento)
      );
      setMunicipiosFiltrados(filteredMunicipios);
      setFilters((prevFilters) => ({ ...prevFilters, municipio: "" }));
    } else {
      setMunicipiosFiltrados([]);
    }
  }, [filters.departamento, municipios]);

  const formatDate = (date, formatType = 'display') => {
    if (!date || date === '') {
      return '';
    }

    let d;

    if (typeof date === 'string') {
      if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = date.split('-').map(num => parseInt(num, 10));
        d = new Date(year, month - 1, day);
      } else {
        d = new Date(date);
      }
    } else {
      d = new Date(date);
    }

    if (isNaN(d.getTime())) {
      return '';
    }

    if (formatType === 'filename') {
      return `${d.getFullYear()}${(d.getMonth() + 1).toString().padStart(2, '0')}${d.getDate().toString().padStart(2, '0')}`;
    }

    return d.toISOString().split('T')[0];
  };

  const isEmpty = (value) => {
    return value === null || value === undefined || value === "";
  };

  const getMissingFields = (representante) => {
    const missingFields = [];
    const camposIgnorados = [
      "Asignación",
      "Organización",
      "Observaciones",
      "Cooperativa",
      "Número",
      "Tomo",
      "Libro",
      "Fecha Constancia CONSUCOOP",
      "Fotografía",
      "Categorías",
    ];

    columns.forEach((col) => {
      if (camposIgnorados.includes(col.header)) {
        return;
      }

      let campoVacio = false;

      if (col.field === "nombre_asociacion") {
        campoVacio = isEmpty(representante.cod_asociacion);
      } else if (col.field === "NombreDepartamento") {
        campoVacio = isEmpty(representante.cod_depto);
      } else if (col.field === "NombreMunicipio") {
        campoVacio = isEmpty(representante.cod_municipio);
      } else if (col.field === "telefono") {
        campoVacio = isEmpty(representante.telefono);
      } else if (col.field === "DNI") {
        campoVacio = isEmpty(representante.DNI);
      } else if (col.field === "cod_cooperativa") {
        campoVacio = false;
      } else {
        campoVacio = isEmpty(representante[col.field]);
      }

      if (campoVacio) {
        missingFields.push(col.header);
      }
    });

    return missingFields;
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const filteredRepresentantes = useMemo(() => {
    return clasificarRepresentantes.filter((representante) => {
      const filterBySearch = representante.nombre_representante
        .toLowerCase()
        .includes(filters.search.toLowerCase());
      const filterByAsociacion = filters.asociacion
        ? representante.cod_asociacion === parseInt(filters.asociacion)
        : true;
      const filterByDepartamento = filters.departamento
        ? String(representante.cod_depto) === String(filters.departamento)
        : true;
      const filterByMunicipio = filters.municipio
        ? String(representante.cod_municipio) === String(filters.municipio)
        : true;
      const filterByFechaInicial = filters.fechaInicial
        ? representante.fecha_vencimiento >= filters.fechaInicial
        : true;
      const filterByFechaFinal = filters.fechaFinal
        ? representante.fecha_vencimiento <= filters.fechaFinal
        : true;
      const filterByEstatus = filters.estatus.length > 0
        ? filters.estatus.includes(representante.estatus)
        : true;
      const filterByEstadoEleccion = filters.estadoEleccion.length > 0
        ? filters.estadoEleccion.includes(representante.estadoEleccion)
        : true;

      return (
        filterBySearch &&
        filterByAsociacion &&
        filterByDepartamento &&
        filterByMunicipio &&
        filterByFechaInicial &&
        filterByFechaFinal &&
        filterByEstatus &&
        filterByEstadoEleccion
      );
    });
  }, [clasificarRepresentantes, filters]);

  const estadisticas = useMemo(() => {
    return {
      total: filteredRepresentantes.length,
      vigentes: filteredRepresentantes.filter((r) => r.estatus === 'VIGENTE').length,
      vencidas: filteredRepresentantes.filter((r) => r.estatus === 'VENCIDA').length,
      inhabilitados: filteredRepresentantes.filter((r) => r.estatus === 'INHABILITADO').length,
    };
  }, [filteredRepresentantes]);

  const exportToExcel = () => {
    try {
      if (Object.keys(selectedColumns).length === 0) {
        setSnackbarMessage("Por favor seleccione al menos una columna para exportar.");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
        return;
      }

      if (filteredRepresentantes.length === 0) {
        setSnackbarMessage("No hay datos para exportar. Ajuste los filtros e intente de nuevo.");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
        return;
      }

      const tableHeaders = Object.values(selectedColumns).map(col => col.header);

      const tableData = filteredRepresentantes.map((representante) =>
        Object.values(selectedColumns).map((col) => {
          let value;
          if (col.field === "NombreDepartamento") {
            value = getNombreDepartamento(representante.cod_depto);
          } else if (col.field === "NombreMunicipio") {
            value = getNombreMunicipio(representante.cod_municipio);
          } else if (col.field === "nombre_asociacion") {
            value = getNombreAsociacion(representante.cod_asociacion);
          } else if (col.field === "cod_cooperativa") {
            value = getNombreCooperativa(representante.cod_cooperativa);
          } else if (col.field === "categorias") {
            value = getCategoriasTexto(representante);
          } else if (col.field === "estadoEleccion") {
            value = representante.estadoEleccion || "";
          } else if (col.field.includes("fecha")) {
            value = representante[col.field] ? formatDate(representante[col.field]) : "";
          } else {
            value = representante[col.field] || "";
          }
          return value;
        })
      );

      const wb = XLSX.utils.book_new();
      const currentDate = new Date();
      
      const headerRow = [`REPRESENTANTES MUNICIPALES - Reporte generado: ${formatDate(currentDate)}`];
      const subHeaderRow = [`Total registros: ${filteredRepresentantes.length}`];
      const emptyRow = [""];

      const sheetData = [
        headerRow,
        subHeaderRow,
        emptyRow,
        tableHeaders,
        ...tableData
      ];

      const ws = XLSX.utils.aoa_to_sheet(sheetData);
      ws['!merges'] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: tableHeaders.length - 1 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: tableHeaders.length - 1 } }
      ];

      XLSX.utils.book_append_sheet(wb, ws, "Representantes_Municipales");

      const excelFile = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(
        new Blob([excelFile], { type: "application/octet-stream" }),
        `Representantes_Municipales_${formatDate(currentDate, 'filename')}.xlsx`
      );

      setSnackbarMessage("Archivo Excel generado exitosamente.");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
    } catch (error) {
      setSnackbarMessage("Error al generar archivo Excel: " + error.message);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };

  const exportToPDF = (preview = false) => {
    try {
      if (Object.keys(selectedColumns).length === 0) {
        setSnackbarMessage("Por favor seleccione al menos una columna para exportar.");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
        return;
      }

      // Validar que no se seleccionen más de 9 columnas para que el PDF se vea bien
      if (Object.keys(selectedColumns).length > 9) {
        setSnackbarMessage("Por favor seleccione máximo 9 columnas para exportar a PDF. Actualmente tiene " + Object.keys(selectedColumns).length + " columnas seleccionadas.");
        setSnackbarSeverity("warning");
        setOpenSnackbar(true);
        return;
      }

      const tableHeaders = Object.values(selectedColumns).map(col => col.header);

      // Ordenar representantes por departamento y luego por municipio
      const representantesOrdenados = [...filteredRepresentantes].sort((a, b) => {
        const deptoA = getNombreDepartamento(a.cod_depto);
        const deptoB = getNombreDepartamento(b.cod_depto);

        // Primero ordenar por departamento
        if (deptoA < deptoB) return -1;
        if (deptoA > deptoB) return 1;

        // Si son del mismo departamento, ordenar por municipio
        const muniA = getNombreMunicipio(a.cod_municipio);
        const muniB = getNombreMunicipio(b.cod_municipio);

        if (muniA < muniB) return -1;
        if (muniA > muniB) return 1;

        return 0;
      });

      const tableData = representantesOrdenados.map((representante) => {
        return Object.values(selectedColumns).map((col) => {
          if (col.field === "NombreDepartamento") {
            return getNombreDepartamento(representante.cod_depto);
          } else if (col.field === "NombreMunicipio") {
            return getNombreMunicipio(representante.cod_municipio);
          } else if (col.field === "nombre_asociacion") {
            return getNombreAsociacion(representante.cod_asociacion);
          } else if (col.field === "cod_cooperativa") {
            return getNombreCooperativa(representante.cod_cooperativa);
          } else if (col.field === "categorias") {
            return getCategoriasTexto(representante);
          } else if (col.field === "estadoEleccion") {
            return representante.estadoEleccion || "";
          } else if (col.field.includes("fecha")) {
            return representante[col.field] ? formatDate(representante[col.field]) : "";
          } else {
            return representante[col.field] || "";
          }
        });
      });

      const doc = new jsPDF({ orientation: 'landscape' });

      // Agregar encabezado
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.text('FONDO CAFETERO NACIONAL', doc.internal.pageSize.getWidth() / 2, 15, { align: 'center' });

      doc.setFontSize(12);
      doc.setFont(undefined, 'normal');
      doc.text('Representantes Municipales', doc.internal.pageSize.getWidth() / 2, 22, { align: 'center' });

      // Agregar total de registros
      doc.setFontSize(10);
      doc.text(`Total de registros: ${filteredRepresentantes.length}`, doc.internal.pageSize.getWidth() / 2, 28, { align: 'center' });

      doc.autoTable({
        startY: 33,
        head: [tableHeaders],
        body: tableData,
        theme: 'striped',
        styles: {
          fontSize: 8,
          cellPadding: 2,
          overflow: 'linebreak',
          cellWidth: 'wrap',
          minCellHeight: 8
        },
        headStyles: {
          fillColor: [126, 90, 60],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 9,
          halign: 'center',
          valign: 'middle'
        },
        columnStyles: {
          // Ajustar anchos según el tipo de contenido
          0: { cellWidth: 'auto' },
          1: { cellWidth: 'auto' },
          2: { cellWidth: 'auto' },
          3: { cellWidth: 'auto' },
          4: { cellWidth: 'auto' },
          5: { cellWidth: 'auto' },
          6: { cellWidth: 'auto' },
          7: { cellWidth: 'auto' },
          8: { cellWidth: 'auto' }
        },
        margin: { left: 10, right: 10 }
      });

      if (preview) {
        const pdfBlob = doc.output('blob');
        const pdfUrl = URL.createObjectURL(pdfBlob);
        setPreviewData(pdfUrl);
        setPreviewOpen(true);
      } else {
        doc.save(`Representantes_Municipales_${formatDate(new Date(), 'filename')}.pdf`);
        setSnackbarMessage("Archivo PDF generado exitosamente.");
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
      }
    } catch (error) {
      setSnackbarMessage("Error al generar PDF: " + error.message);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };

  const exportarHistorico = () => {
    try {
      const wb = XLSX.utils.book_new();
      const currentDate = new Date();
      
      let exportarNuevos = representantesNuevos;
      let exportarReelectos = representantesReelectos;

      if (exportarNuevos.length > 0) {
        const datosNuevos = exportarNuevos.map(rep => ({
          'Tipo': 'NUEVO',
          'DNI': rep.dni,
          'Nombre': rep.nombre_representante,
          'Asociación': getNombreAsociacion(rep.cod_asociacion),
          'Departamento': getNombreDepartamento(rep.cod_depto),
          'Municipio': getNombreMunicipio(rep.cod_municipio),
          'Fecha Elección': formatDate(rep.fecha_eleccion),
          'Estado': rep.estatus
        }));
        
        const wsNuevos = XLSX.utils.json_to_sheet(datosNuevos);
        XLSX.utils.book_append_sheet(wb, wsNuevos, 'Representantes Nuevos');
      }
      
      if (exportarReelectos.length > 0) {
        const datosReelectos = exportarReelectos.map(rep => ({
          'Tipo': 'REELECCIÓN',
          'DNI': rep.dni,
          'Nombre': rep.nombre_representante,
          'Asociación': getNombreAsociacion(rep.cod_asociacion),
          'Departamento': getNombreDepartamento(rep.cod_depto),
          'Municipio': getNombreMunicipio(rep.cod_municipio),
          'Periodo Actual': `${rep.numeroPeriodo}° periodo`,
          'Veces Reelecto': rep.vecesReelecto,
          'Fecha Elección': formatDate(rep.fecha_eleccion),
          'Estado': rep.estatus
        }));
        
        const wsReelectos = XLSX.utils.json_to_sheet(datosReelectos);
        XLSX.utils.book_append_sheet(wb, wsReelectos, 'Representantes Reelectos');
      }

      if (exportarNuevos.length > 0 || exportarReelectos.length > 0) {
        const excelFile = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        saveAs(
          new Blob([excelFile], { type: 'application/octet-stream' }),
          `Historico_Completo_${formatDate(currentDate, 'filename')}.xlsx`
        );
        
        setSnackbarMessage('Histórico exportado exitosamente.');
        setSnackbarSeverity('success');
        setOpenSnackbar(true);
      } else {
        setSnackbarMessage('No hay datos históricos para exportar.');
        setSnackbarSeverity('warning');
        setOpenSnackbar(true);
      }
      
    } catch (error) {
      setSnackbarMessage('Error al exportar histórico: ' + error.message);
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSelectAll = () => {
    const allColumnsSelection = columns.reduce((acc, col) => {
      acc[col.field] = col;
      return acc;
    }, {});
    setSelectedColumns(allColumnsSelection);
  };

  const handleClearAll = () => {
    setSelectedColumns({});
    setFilters({
      search: "",
      asociacion: "",
      departamento: "",
      municipio: "",
      fechaInicial: "",
      fechaFinal: "",
      estatus: [],
      estadoEleccion: [],
    });
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    if (previewData) {
      URL.revokeObjectURL(previewData);
    }
    setPreviewData(null);
  };

  const renderTablaHistorico = (datos, tipo) => {
    if (datos.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="textSecondary">
            No se encontraron representantes con los filtros aplicados
          </Typography>
        </Box>
      );
    }

    return (
      <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: theme.primary.main, color: 'white', fontWeight: 'bold' }}>DNI</TableCell>
              <TableCell sx={{ backgroundColor: theme.primary.main, color: 'white',fontWeight: 'bold' }}>Nombre</TableCell>
              <TableCell sx={{ backgroundColor: theme.primary.main, color: 'white', fontWeight: 'bold' }}>Asociación</TableCell>
              <TableCell sx={{ backgroundColor: theme.primary.main, color: 'white', fontWeight: 'bold' }}>Municipio</TableCell>
              {tipo === 'REELECCIÓN' && (
                <TableCell sx={{ backgroundColor: theme.primary.main, color: 'white', fontWeight: 'bold' }} align="center">Periodo</TableCell>
              )}
              <TableCell sx={{ backgroundColor: theme.primary.main, color: 'white', fontWeight: 'bold' }}>Fecha Elección</TableCell>
              <TableCell sx={{ backgroundColor: theme.primary.main, color: 'white', fontWeight: 'bold' }} align="center">Estado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {datos.map((rep, index) => (
              <TableRow 
                key={index}
                sx={{ 
                  '&:nth-of-type(odd)': { backgroundColor: theme.background.default },
                  '&:hover': { backgroundColor: theme.background.card }
                }}
              >
                <TableCell sx={{ fontWeight: 'bold' }}>{rep.dni}</TableCell>
                <TableCell>{rep.nombre_representante}</TableCell>
                <TableCell>{getNombreAsociacion(rep.cod_asociacion)}</TableCell>
                <TableCell>{getNombreMunicipio(rep.cod_municipio)}</TableCell>
                {tipo === 'REELECCIÓN' && (
                  <TableCell align="center">
                    <Chip 
                      label={`${rep.numeroPeriodo}° periodo`} 
                      size="small" 
                      sx={{ backgroundColor: theme.secondary.main, color: 'white', fontWeight: 'bold' }}
                    />
                  </TableCell>
                )}
                <TableCell>{formatDate(rep.fecha_eleccion)}</TableCell>
                <TableCell align="center">
                  <Chip 
                    label={rep.estatus} 
                    size="small"
                    sx={{
                      backgroundColor: 
                        rep.estatus === 'VIGENTE' ? theme.success.main : 
                        rep.estatus === 'VENCIDA' ? theme.error.main : 
                        theme.text.disabled,
                      color: 'white'
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: theme.background.default }}>
      {/* PANEL LATERAL FIJO DE COLUMNAS VISIBLES */}
      <Drawer
        variant="permanent"
        anchor="left"
        sx={{
          width: sidebarOpen ? 248 : 44,
          flexShrink: 0,
          transition: 'width 0.2s ease',
          '& .MuiDrawer-paper': {
            width: sidebarOpen ? 248 : 44,
            boxSizing: 'border-box',
            position: 'relative',
            border: 'none',
            borderRight: `1px solid ${theme.primary.light}25`,
            backgroundColor: theme.background.card,
            overflowX: 'hidden',
            transition: 'width 0.2s ease',
            p: sidebarOpen ? 2 : 1
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: sidebarOpen ? 'flex-end' : 'center', mb: 1 }}>
          <IconButton
            size="small"
            onClick={() => setSidebarOpen((prev) => !prev)}
            sx={{
              color: theme.secondary.main,
              backgroundColor: `${theme.secondary.main}14`,
              '&:hover': { backgroundColor: `${theme.secondary.main}28` },
            }}
          >
            {sidebarOpen ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </IconButton>
        </Box>

        {!sidebarOpen && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <TuneIcon sx={{ fontSize: 20, color: theme.secondary.main }} />
          </Box>
        )}

        {sidebarOpen && (
        <>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
          <TuneIcon sx={{ fontSize: 18, color: theme.secondary.main }} />
          <Typography variant="body2" sx={{ color: theme.primary.dark, fontWeight: 700 }}>
            Columnas
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="caption" sx={{ color: theme.text.secondary }}>
            {Object.keys(selectedColumns).length}/{columns.length}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 0.75, my: 1.25 }}>
          <Button
            size="small"
            fullWidth
            onClick={handleSelectAll}
            sx={{
              borderRadius: 1.5,
              color: theme.secondary.dark,
              backgroundColor: `${theme.secondary.main}14`,
              fontSize: '0.7rem',
              textTransform: 'none',
              py: 0.5,
              '&:hover': { backgroundColor: `${theme.secondary.main}24` },
            }}
          >
            Todas
          </Button>
          <Button
            size="small"
            fullWidth
            onClick={handleClearAll}
            sx={{
              borderRadius: 1.5,
              color: theme.text.secondary,
              backgroundColor: '#00000008',
              fontSize: '0.7rem',
              textTransform: 'none',
              py: 0.5,
              '&:hover': { backgroundColor: '#00000012' },
            }}
          >
            Ninguna
          </Button>
        </Box>

        <Stack spacing={0.75}>
          {COLUMN_GROUPS.map((group) => {
            const groupColumns = columns.filter((col) => group.fields.includes(col.field));
            if (groupColumns.length === 0) return null;
            const activeCount = groupColumns.filter((col) => selectedColumns[col.field]).length;

            return (
              <Accordion
                key={group.title}
                disableGutters
                elevation={0}
                square={false}
                sx={{
                  border: '1px solid #00000010',
                  borderRadius: '10px !important',
                  overflow: 'hidden',
                  backgroundColor: '#FFFFFF',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    minHeight: 38,
                    px: 1.25,
                    '& .MuiAccordionSummary-content': {
                      my: 0.5,
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      pr: 1,
                    },
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600, color: theme.text.primary }}>
                    {group.title}
                  </Typography>
                  <Chip
                    label={`${activeCount}/${groupColumns.length}`}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.62rem',
                      fontWeight: 700,
                      backgroundColor: activeCount > 0 ? `${theme.primary.main}18` : '#f0f0f0',
                      color: activeCount > 0 ? theme.primary.main : theme.text.secondary,
                    }}
                  />
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, pb: 0.75, px: 1 }}>
                  <Stack spacing={0}>
                    {groupColumns.map((col) => {
                      const active = Boolean(selectedColumns[col.field]);
                      return (
                        <Box
                          key={col.field}
                          onClick={() => toggleColumnSelection(col)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            pl: 1,
                            pr: 0.25,
                            py: 0.25,
                            borderRadius: 1.5,
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: '#00000008' },
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: active ? theme.primary.dark : theme.text.secondary,
                              fontWeight: active ? 600 : 400,
                            }}
                          >
                            {col.header}
                          </Typography>
                          <Checkbox
                            checked={active}
                            size="small"
                            onChange={() => toggleColumnSelection(col)}
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              p: 0.4,
                              color: theme.primary.light,
                              '&.Mui-checked': { color: theme.primary.main },
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
          <FilterAltIcon sx={{ fontSize: 18, color: theme.secondary.main }} />
          <Typography variant="body2" sx={{ color: theme.primary.dark, fontWeight: 700 }}>
            Filtros
          </Typography>
        </Box>

        <Stack spacing={1.25}>
          <TextField
            label="Buscar representante"
            fullWidth
            size="small"
            value={filters.search}
            onChange={(e) => {
              setFilters({ ...filters, search: e.target.value });
              setShowSuggestions(true);
            }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: theme.primary.light, mr: 0.5, fontSize: 18 }} />,
              sx: { backgroundColor: 'white', borderRadius: 1, fontSize: '0.8rem' }
            }}
          />

          <FormControl fullWidth size="small">
            <InputLabel>Asociación</InputLabel>
            <Select
              label="Asociación"
              value={filters.asociacion}
              onChange={(e) => setFilters({ ...filters, asociacion: e.target.value })}
              sx={{ backgroundColor: 'white', borderRadius: 1 }}
            >
              <MenuItem value="">Todas</MenuItem>
              {asociaciones.map((asociacion) => (
                <MenuItem key={asociacion.cod_asociacion} value={asociacion.cod_asociacion}>
                  {asociacion.nombre_abreviado}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Departamento</InputLabel>
            <Select
              label="Departamento"
              value={filters.departamento}
              onChange={(e) => setFilters({ ...filters, departamento: e.target.value })}
              sx={{ backgroundColor: 'white', borderRadius: 1 }}
            >
              <MenuItem value="">Todos</MenuItem>
              {departamentos.map((depto) => (
                <MenuItem key={depto.CodigoDepartamento} value={depto.CodigoDepartamento}>
                  {depto.NombreDepartamento}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Municipio</InputLabel>
            <Select
              label="Municipio"
              value={filters.municipio}
              onChange={(e) => setFilters({ ...filters, municipio: e.target.value })}
              sx={{ backgroundColor: 'white', borderRadius: 1 }}
            >
              <MenuItem value="">Todos</MenuItem>
              {municipiosFiltrados.map((municipio) => (
                <MenuItem key={municipio.CodigoMunicipio} value={municipio.CodigoMunicipio}>
                  {municipio.NombreMunicipio}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <Typography variant="caption" sx={{ color: theme.text.secondary, fontWeight: 600, display: 'block', mb: 0.5 }}>
              Estatus
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {['VIGENTE', 'VENCIDA', 'INHABILITADO'].map((status) => {
                const active = filters.estatus.includes(status);
                const color = ESTATUS_COLOR[status] || theme.text.disabled;
                return (
                  <Chip
                    key={status}
                    label={status}
                    size="small"
                    onClick={() => handleStatusChange(status, 'estatus')}
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.65rem',
                      height: 22,
                      cursor: 'pointer',
                      backgroundColor: active ? color : `${color}14`,
                      color: active ? 'white' : color,
                      border: `1px solid ${color}`,
                      '&:hover': { backgroundColor: active ? color : `${color}28` },
                    }}
                  />
                );
              })}
            </Box>
          </Box>

          <Box>
            <Typography variant="caption" sx={{ color: theme.text.secondary, fontWeight: 600, display: 'block', mb: 0.5 }}>
              Estado de elección
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {['Nuevo', 'Reelegido'].map((estado) => {
                const active = filters.estadoEleccion.includes(estado);
                return (
                  <Chip
                    key={estado}
                    label={estado}
                    size="small"
                    onClick={() => handleStatusChange(estado, 'estadoEleccion')}
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.65rem',
                      height: 22,
                      cursor: 'pointer',
                      backgroundColor: active ? theme.secondary.main : `${theme.secondary.main}14`,
                      color: active ? 'white' : theme.secondary.dark,
                      border: `1px solid ${theme.secondary.main}`,
                      '&:hover': { backgroundColor: active ? theme.secondary.dark : `${theme.secondary.main}28` },
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        </Stack>
        </>
        )}
      </Drawer>

    <Container
      maxWidth={false}
      disableGutters
      sx={{
        py: 3,
        px: { xs: 1, sm: 2, md: 3 },
        mx: 0,
        backgroundColor: theme.background.default,
        borderRadius: 0,
        boxShadow: "0 6px 18px rgba(0, 0, 0, 0.08)",
        my: 0,
        border: 'none',
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        flex: 1,
        minWidth: 0,
        minHeight: '100vh'
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: `linear-gradient(90deg, ${theme.primary.main}, ${theme.secondary.main})`
        }}
      />

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Box sx={{ maxWidth: 1600, mx: 'auto' }}>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 1,
          gap: 2
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '14px',
            background: `linear-gradient(135deg, ${theme.primary.main} 0%, ${theme.primary.dark} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 6px 16px ${theme.primary.main}40`,
            flexShrink: 0
          }}
        >
          <PersonIcon sx={{ fontSize: 30, color: 'white' }} />
        </Box>
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              color: theme.primary.dark,
              fontWeight: 800,
              lineHeight: 1.2
            }}
          >
            Reportería de Representantes
          </Typography>
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={exportToExcel}
            size="small"
            sx={{
              borderRadius: '6px',
              color: theme.success.dark,
              borderColor: theme.success.main,
              '&:hover': {
                backgroundColor: `${theme.success.main}12`,
                borderColor: theme.success.dark,
              }
            }}
            startIcon={<DownloadIcon />}
          >
            Excel
          </Button>

          <Button
            variant="outlined"
            onClick={() => exportToPDF(false)}
            size="small"
            sx={{
              borderRadius: '6px',
              color: theme.error.dark,
              borderColor: theme.error.main,
              '&:hover': {
                backgroundColor: `${theme.error.main}12`,
                borderColor: theme.error.dark,
              }
            }}
            startIcon={<DownloadIcon />}
          >
            PDF
          </Button>
        </Box>
      </Box>

      {/* TARJETAS DE RESUMEN */}
      <Grid container spacing={2} sx={{ my: 3 }}>
        {[
          { label: 'Total encontrados', value: estadisticas.total, color: theme.primary.main, icon: <GroupsIcon /> },
          { label: 'Vigentes', value: estadisticas.vigentes, color: theme.success.main, icon: <CheckCircleIcon /> },
          { label: 'Vencidas', value: estadisticas.vencidas, color: theme.error.main, icon: <ErrorOutlineIcon /> },
          { label: 'Inhabilitados', value: estadisticas.inhabilitados, color: theme.text.disabled, icon: <BlockIcon /> },
        ].map((stat) => (
          <Grid item xs={6} sm={3} key={stat.label}>
            <Card
              sx={{
                p: 2.25,
                borderRadius: 3,
                backgroundColor: '#FFFFFF',
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: '0 10px 24px rgba(0,0,0,0.1)'
                }
              }}
            >
              <Box
                sx={{
                  width: 46,
                  height: 46,
                  borderRadius: '14px',
                  backgroundColor: `${stat.color}18`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                {React.cloneElement(stat.icon, { sx: { color: stat.color, fontSize: 24 } })}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: theme.text.primary, lineHeight: 1.1 }}>
                  {stat.value}
                </Typography>
                <Typography variant="caption" sx={{ color: theme.text.secondary, fontWeight: 500 }}>
                  {stat.label}
                </Typography>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>


      {/* TABLA DE RESULTADOS */}
      {loading ? (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={10}>
          <CircularProgress size={60} style={{ color: theme.primary.main }} />
          <Typography variant="h6" sx={{ mt: 3, color: theme.text.secondary }}>
            Cargando representantes municipales...
          </Typography>
        </Box>
      ) : (
        <Card
          sx={{
            mb: 3,
            overflow: 'hidden',
            borderRadius: 3,
            border: '1px solid #00000010',
            boxShadow: '0 2px 12px rgba(0,0,0,0.05)'
          }}
        >
          <TableContainer sx={{ maxHeight: "70vh" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {Object.values(selectedColumns).map((col) => (
                    <TableCell
                      key={col.field}
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        whiteSpace: 'nowrap',
                        backgroundColor: '#FFFFFF',
                        color: theme.text.secondary,
                        borderBottom: `2px solid ${theme.primary.main}`,
                        position: 'sticky',
                        top: 0,
                        zIndex: 1
                      }}
                    >
                      {col.header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRepresentantes
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((representante) => {
                    const missingFields = getMissingFields(representante);
                    const hasWarning = missingFields.length > 0;

                    return (
                      <TableRow
                        key={representante.cod_representante}
                        onClick={() => handleRowClick(representante)}
                        sx={{
                          '&:nth-of-type(odd)': {
                            backgroundColor: theme.background.card + '40'
                          },
                          '&:hover': {
                            backgroundColor: theme.background.card + '80',
                            cursor: 'pointer'
                          },
                          position: 'relative',
                          ...(hasWarning && {
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '4px',
                              height: '100%',
                              backgroundColor: theme.error.main,
                            }
                          })
                        }}
                      >
                        {Object.values(selectedColumns).map((col, index) => (
                          <TableCell
                            key={col.field}
                            sx={{
                              borderBottom: '1px solid #00000010',
                              color: theme.text.primary,
                              fontSize: '0.8rem',
                            }}
                          >
                            {index === 0 && hasWarning && (
                              <ErrorOutlineIcon
                                sx={{
                                  color: theme.error.main,
                                  fontSize: '1rem',
                                  mr: 1,
                                  verticalAlign: 'middle'
                                }}
                              />
                            )}
                            {col.field === "NombreDepartamento" &&
                              getNombreDepartamento(representante.cod_depto)}
                            {col.field === "NombreMunicipio" &&
                              getNombreMunicipio(representante.cod_municipio)}
                            {col.field === "nombre_asociacion" &&
                              getNombreAsociacion(representante.cod_asociacion)}
                            {col.field === "cod_cooperativa" &&
                              getNombreCooperativa(representante.cod_cooperativa)}
                            {col.field === "categorias" && getCategoriasTexto(representante)}
                            {col.field === "estadoEleccion" && representante.estadoEleccion}
                            {col.field === "estatus" && (
                              <Chip
                                label={representante.estatus || 'SIN ESTADO'}
                                size="small"
                                sx={{
                                  backgroundColor: `${ESTATUS_COLOR[representante.estatus] || theme.text.disabled}18`,
                                  color: ESTATUS_COLOR[representante.estatus] || theme.text.disabled,
                                  fontWeight: 700,
                                  fontSize: '0.7rem'
                                }}
                              />
                            )}
                            {col.field.includes("fecha") && !["estadoEleccion"].includes(col.field)
                              ? representante[col.field]
                                ? formatDate(representante[col.field])
                                : ""
                              : col.field !== "estadoEleccion" &&
                                col.field !== "estatus" &&
                                col.field !== "NombreDepartamento" &&
                                col.field !== "NombreMunicipio" &&
                                col.field !== "nombre_asociacion" &&
                                col.field !== "cod_cooperativa" &&
                                col.field !== "categorias"
                              ? representante[col.field] || ""
                              : ""}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 15, 50, 100]}
            component="div"
            count={filteredRepresentantes.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              color: theme.text.primary,
              backgroundColor: theme.background.paper,
              borderTop: `1px solid ${theme.primary.light}20`,
            }}
          />
        </Card>
      )}

      {/* FOOTER */}
      <Box
        sx={{
          mt: 4,
          pt: 2,
          borderTop: `1px solid ${theme.primary.light}20`,
          display: 'flex',
          justifyContent: 'center'
        }}
      >
        <Typography variant="body2" color={theme.text.secondary}>
          © {new Date().getFullYear()} FONDO CAFETERO NACIONAL. Todos los derechos reservados.
        </Typography>
      </Box>

      </Box>

      {/* DIALOG DE DETALLE DEL REPRESENTANTE */}
      <Dialog
        open={Boolean(selectedRepresentante)}
        onClose={handleDetailClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.25)'
          }
        }}
      >
        {selectedRepresentante && (
          <>
            {/* Encabezado con foto y estado */}
            <Box sx={{
              background: `linear-gradient(135deg, ${theme.primary.main} 0%, ${theme.primary.dark} 100%)`,
              p: 3,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              color: 'white'
            }}>
              <Avatar
                src={selectedRepresentante.fotografia || undefined}
                sx={{
                  width: 72,
                  height: 72,
                  border: '3px solid rgba(255,255,255,0.5)',
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  backgroundColor: theme.secondary.main
                }}
              >
                {(selectedRepresentante.nombre_representante || '?').charAt(0)}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                  {selectedRepresentante.nombre_representante}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.85, mb: 1 }}>
                  DNI: {selectedRepresentante.dni || 'No registrado'}
                </Typography>
                <Chip
                  label={selectedRepresentante.estatus || 'SIN ESTADO'}
                  size="small"
                  sx={{
                    backgroundColor: ESTATUS_COLOR[selectedRepresentante.estatus] || 'rgba(255,255,255,0.25)',
                    color: 'white',
                    fontWeight: 700
                  }}
                />
              </Box>
              <IconButton onClick={handleDetailClose} sx={{ color: 'white', alignSelf: 'flex-start' }}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Banner de completitud */}
            <Box sx={{
              py: 1.25,
              px: 3,
              backgroundColor: camposFaltantes.length > 0 ? `${theme.error.main}15` : `${theme.success.main}15`,
              borderBottom: `1px solid ${camposFaltantes.length > 0 ? theme.error.light : theme.success.light}`,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              {camposFaltantes.length > 0 ? (
                <WarningIcon sx={{ color: theme.error.main, fontSize: 20 }} />
              ) : (
                <Box sx={{ color: theme.success.main, fontSize: 18, fontWeight: 700 }}>✓</Box>
              )}
              <Typography variant="body2" sx={{ fontWeight: 600, color: camposFaltantes.length > 0 ? theme.error.dark : theme.success.dark }}>
                {camposFaltantes.length > 0
                  ? `${camposFaltantes.length} campos pendientes de subsanar`
                  : "Registro completo"}
              </Typography>
            </Box>

            <DialogContent sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <DetailSection title="Ubicación">
                    <DetailField label="Asociación" value={getNombreAsociacion(selectedRepresentante.cod_asociacion)} />
                    <DetailField label="Departamento" value={getNombreDepartamento(selectedRepresentante.cod_depto)} />
                    <DetailField label="Municipio" value={getNombreMunicipio(selectedRepresentante.cod_municipio)} />
                    <DetailField label="Domicilio" value={selectedRepresentante.domicilio} />
                    <DetailField label="Ubicación de finca" value={selectedRepresentante.ubicacion_finca} />
                  </DetailSection>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DetailSection title="Contacto y elección">
                    <DetailField label="Teléfono" value={selectedRepresentante.telefono} />
                    <DetailField label="Fecha de elección" value={formatDate(selectedRepresentante.fecha_eleccion)} />
                    <DetailField label="Fecha de vencimiento" value={formatDate(selectedRepresentante.fecha_vencimiento)} />
                    <DetailField label="Estado de elección" value={selectedRepresentante.estadoEleccion} />
                  </DetailSection>
                </Grid>

                <Grid item xs={12}>
                  <DetailSection title="Categorías">
                    {selectedRepresentante.categorias?.filter(cat => cat.Estado !== 'Inactivo').length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {selectedRepresentante.categorias.filter(cat => cat.Estado !== 'Inactivo').map((categoria, index) => (
                          <Chip
                            key={index}
                            label={categoria.Cargo
                              ? `${categoria.Descripcion || `Categoría ${categoria.Id || categoria.CategoriaFK}`} · ${categoria.Cargo}`
                              : (categoria.Descripcion || `Categoría ${categoria.Id || categoria.CategoriaFK}`)}
                            size="small"
                            sx={{
                              backgroundColor: categoria.Id === 3 || categoria.CategoriaFK === 3 ? theme.primary.main : theme.secondary.light,
                              color: 'white',
                              fontWeight: 600
                            }}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ color: theme.text.secondary, fontStyle: 'italic' }}>
                        Sin categorías activas asignadas
                      </Typography>
                    )}
                  </DetailSection>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DetailSection title="Datos del acta">
                    <DetailField label="Clave IHCAFE" value={selectedRepresentante.clave_IHCAFE} />
                    <DetailField label="Cooperativa" value={getNombreCooperativa(selectedRepresentante.cod_cooperativa)} />
                    <DetailField
                      label="Número / Tomo / Libro"
                      value={[selectedRepresentante.numero, selectedRepresentante.tomo, selectedRepresentante.libro].filter(Boolean).join(' / ')}
                    />
                    <DetailField label="Fecha de constancia" value={formatDate(selectedRepresentante.fecha_constancia)} />
                  </DetailSection>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DetailSection title="Otros">
                    <DetailField label="Reporte de cosecha" value={selectedRepresentante.reporte_cosecha} />
                    <DetailField label="Asignación" value={selectedRepresentante.asignacion} />
                    <DetailField label="Organización" value={selectedRepresentante.organizacion} />
                    {selectedRepresentante.observaciones && (
                      <DetailField label="Observaciones" value={selectedRepresentante.observaciones} />
                    )}
                  </DetailSection>
                </Grid>

                {camposFaltantes.length > 0 && (
                  <Grid item xs={12}>
                    <DetailSection title="Campos pendientes de completar">
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {camposFaltantes.map((campo, index) => (
                          <Chip
                            key={index}
                            label={campo}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: theme.error.light,
                              color: theme.error.main,
                              fontSize: '0.75rem'
                            }}
                          />
                        ))}
                      </Box>
                    </DetailSection>
                  </Grid>
                )}
              </Grid>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2, borderTop: `1px solid ${theme.primary.light}20` }}>
              <Button
                onClick={handleDetailClose}
                variant="outlined"
                sx={{
                  borderRadius: 1,
                  color: theme.primary.main,
                  borderColor: theme.primary.main
                }}
              >
                Cerrar
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* DIALOG PREVIEW PDF */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
          }
        }}
      >
        <DialogTitle sx={{ backgroundColor: theme.primary.main, color: 'white' }}>
          Vista previa del PDF - Representantes Municipales
          <IconButton
            onClick={handleClosePreview}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'white'
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {previewData && (
            <iframe
              ref={iframeRef}
              src={previewData}
              style={{ width: '100%', height: '75vh', border: 'none' }}
              title="Vista previa PDF"
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, backgroundColor: theme.background.card }}>
          <Button
            onClick={handleClosePreview}
            variant="outlined"
            sx={{
              borderRadius: 1,
              color: theme.text.secondary,
              borderColor: theme.text.secondary
            }}
          >
            Cerrar
          </Button>
          {previewData && (
            <Button
              onClick={() => {
                window.open(previewData, '_blank');
              }}
              variant="contained"
              sx={{
                borderRadius: 1,
                backgroundColor: theme.primary.main,
                '&:hover': {
                  backgroundColor: theme.primary.dark
                }
              }}
            >
              Abrir en nueva pestaña
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
    </Box>
  );
};

export default Reporteria;